// This program has been developed by students from the bachelor Computer Science at
// Utrecht University within the Software Project course.
// © Copyright Utrecht University (Department of Information and Computing Sciences)

import React from 'react'
import './InfoPage.css'
import '../../shared/shared.css'
import uuLogo from '../../images/uu-logo.svg'
import mebLogo from '../../images/meb-logo.svg'
import { useLocation, useNavigate, Link } from 'react-router-dom'
import config from '../../config/config'

// Function based component rendering the info page
function InfoPage() {
  const [citationCopied, setCitationCopied] = React.useState(false)
  const { hash } = useLocation()
  const citation = 'European Medicines Regulatory Database (EMRD). Utrecht: Utrecht University and Dutch Medicines Evaluation Board. Available from: https://emrd.uu.nl/.'

  React.useEffect(() => {
    if (hash === '#citation') {
      document.getElementById('citation')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [hash])

  const copyCitation = async () => {
    if (!navigator.clipboard?.writeText) {
      console.error('Copying the EMRD citation is not supported in this browser.')
      return
    }

    try {
      await navigator.clipboard.writeText(citation)
      setCitationCopied(true)
    } catch (error) {
      console.error('Unable to copy the EMRD citation.', error)
    }
  }

  return (
    // Infopage components, contains article containers
    <div className="med-info-content">
      <section className="med-content-container">
        <h1>About the EMRD project</h1>
        <hr className="med-top-separator" />
        <p>
          The <strong>E</strong>uropean <strong>M</strong>edicines <strong>R</strong>egulatory <strong>D</strong>atabase (<strong>EMRD</strong>) is a free-to-access interactive dashboard that stores and displays daily-updated information on the regulation of medicines for human use in the European Union (EU). EMRD aggregates information from various regulatory sources and documents such as public assessment reports, legal decisions, and medicine labels. It offers a comprehensive database of how medicines for human use in the EU are regulated during their lifecycle, focusing on data from key events such as marketing authorisation procedures, orphan designations, and post-authorisation regulatory actions.
        </p>
        <p>
          The intended use of EMRD is to support researchers studying drug development, regulation, and use in research fields such as drug regulatory science, drug innovation, clinical pharmacology, and pharmacoepidemiology. EMRD also intends to serve other stakeholders such as medicine regulators, developers, and patients, as well as students who would like to learn more about the European medicines regulatory system and regulation of individual medicines.
        </p>
      </section>
      <section className="med-content-container">
        <h1>Funding</h1>
        <p>
          Development of the EMRD was funded in part by the Dutch National Growth Fund (NGF) through the Workstream Regulatory Innovation of the Oncode Accelerator project (reference number NGFOP2201), as well as by the Dutch Cancer Society (KWF; project number 9086). Early development was made possible by a Veni grant (grant number 451-15-037) from the Netherlands Organisation for Scientific Research (NWO).
        </p>
      </section>

      <section className="med-content-container">
        <h2>About the EMRD team</h2>
        <hr className="med-top-separator" />
        <h3>Core team</h3>
        <p className="med-institution-info">
          EMRD is a joint initiative of Utrecht University (UU) and the Dutch
          Medicines Evaluation Board (MEB). The EMRD project was initiated and is
          currently led by:
        </p>
        <ul>
          <li>
            <a
              href="https://www.uu.nl/staff/LTBloem"
              target="_blank"
              rel="noopener noreferrer"
            >
              Dr. Lourens Bloem
            </a>{" "}
            (UU)
          </li>
          <li>
            <a
              href="https://www.uu.nl/staff/VAHaverhoek"
              target="_blank"
              rel="noopener noreferrer"
            >
              Vincent Haverhoek, MSc
            </a>{" "}
            (UU)
          </li>
          <li>
            <a
              href="https://www.uu.nl/staff/JHoekman"
              target="_blank"
              rel="noopener noreferrer"
            >
              Prof. dr. Jarno Hoekman
            </a>{" "}
            (UU)
          </li>
          <li>
            <a
              href="https://www.uu.nl/staff/AMGPasmooij"
              target="_blank"
              rel="noopener noreferrer"
            >
              Dr. Marjon Pasmooij
            </a>{" "}
            (MEB, UU)
          </li>
          <li>
            <a
              href="https://www.linkedin.com/in/stefan-verweij-347776163/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Stefan Verweij, MSc
            </a>{" "}
            (MEB)
          </li>
        </ul>

        <div className="med-institution-logos">
          <img
            src={uuLogo}
            alt="Logo of Utrecht University"
            className="med-institution-logo"
          />
          <img
            src={mebLogo}
            alt="Logo of the Medicines Evaluation Board"
            className="med-institution-logo"
          />
        </div>

        <h3>Acknowledgements</h3>
        <p>
          Many individuals have supported EMRD during its development and
          contributed to bringing it to life. Special thanks go out to students from
          Utrecht University's Bachelor's programme in Information Science, organised
          in project teams MedCTRL and MediSee, who greatly contributed to the
          development of the EMRD as part of the Software Project course. In
          addition, interns at the Dutch Medicines Evaluation Board greatly
          contributed to the validation of the EMRD workflows and data.
        </p>
      </section>

      <section className="med-content-container">
        <h2>About the EMRD data</h2>

        <h3>Data provenance</h3>
        <p>
          EMRD is primarily comprised of data sourced from the{" "}
          <a
            href="https://www.ema.europa.eu/en/homepage"
            target="_blank"
            rel="noopener noreferrer"
          >
            European Medicines Agency
          </a>{" "}
          and the{" "}
          <a
            href="https://ec.europa.eu/health/documents/community-register/html/index_en.htm"
            target="_blank"
            rel="noopener noreferrer"
          >
            Union Register of Medicinal Products
          </a>{" "}
          of the European Commission, along with manually curated and validated data
          from various other sources. Metadata including data sources for all
          variables can be found in the{" "}
          <a
            href="/Metadata-description-EMRD-20261006.pdf"
            target="_blank"
            rel="noopener noreferrer"
          >
            downloadable metadata file (PDF)
          </a>
          .
        </p>

        <h3>License</h3>
        <p>
          All data and metadata downloadable from this dashboard are available under
          a{" "}
          <a
            href="https://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Creative Commons Attribution 4.0 International license
          </a>
          . Data is maintained and managed as much as possible using FAIR principles.
        </p>

        <h3>Future Release Note</h3>
        <p>
          Version 1.0 of the European Medicines Regulatory Database was released on
          TBD.
        </p>

        <h3>Disclaimer</h3>
        <p>
          EMRD data is intended for educational and scientific research purposes
          only. Effort has been made to ensure the accuracy and completeness of the
          information that is provided on this website, which is also described in
          the corresponding data publication(TBD). Utrecht University and the Dutch
          Medicines Evaluation Board make no warranties regarding errors in the data,
          and assume no responsibility for the use of the EMRD.
        </p>
      </section>

      <section
        id="citation"
        className={`med-content-container med-citation-section${hash === '#citation' ? ' is-highlighted' : ''}`}
      >
        <h1>How to cite the EMRD</h1>
        <p>
          We ask that users who make use of the EMRD cite the published EMRD data
          publication in any resulting scientific publications or reports.
        </p>
        <p>
          In the meantime, the EMRD should be cited as follows:
        </p>
        <div className="med-citation-row">
          <p className="med-citation">
            "European Medicines Regulatory Database (EMRD). Utrecht: Utrecht
            University and Dutch Medicines Evaluation Board. Available from:{" "}
            <a href="https://emrd.uu.nl/" target="_blank" rel="noopener noreferrer">
              https://emrd.uu.nl/
            </a>
            ."
          </p>
        </div>
        <button
          type="button"
          className="med-copy-citation-button"
          onClick={copyCitation}
          aria-label={citationCopied ? 'EMRD citation copied' : 'Copy EMRD citation'}
        >
          <i
            className={`bx ${citationCopied ? 'bx-check' : 'bx-copy'}`}
            aria-hidden="true"
          ></i>
          {citationCopied ? 'Copied' : 'Copy citation'}
        </button>
      </section>
    </div>
  )
}

export default InfoPage
